import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  ChevronDown,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  X,
  Check,
} from "lucide-react";
import type {
  RequestRecord,
  ShortcutFilter,
  DecisionFilter,
  InspectionFilter,
  SortField,
  SortOrder,
  TimeRangeKey,
  SidebarCounts,
} from "../../types/telemetry.ts";
import { INSPECTION_FAILURE_STATUSES } from "../../utils/aggregation.ts";
import { formatDateTimeIST } from "../../utils/dateTime.ts";
import { Skeleton } from "../common/Skeleton.tsx";
import { RequestDetailPanel } from "./RequestDetailPanel.tsx";

const TIME_RANGE_OPTIONS: { key: TimeRangeKey; label: string }[] = [
  { key: "15m", label: "Last 15 min" },
  { key: "60m", label: "Last 60 min" },
  { key: "24h", label: "Last 24 h" },
  { key: "all", label: "All records" },
];

const INSPECTION_FILTER_OPTIONS: { value: InspectionFilter; label: string }[] = [
  { value: "ALL", label: "All statuses" },
  { value: "SUCCESS", label: "SUCCESS" },
  { value: "NOT_RUN", label: "NOT_RUN" },
  { value: "TIMEOUT", label: "TIMEOUT" },
  { value: "CONNECTION_FAILURE", label: "CONNECTION_FAILURE" },
  { value: "PARSING_FAILURE", label: "PARSING_FAILURE" },
  { value: "UNEXPECTED_STATUS", label: "UNEXPECTED_STATUS" },
  { value: "MISSING_METADATA", label: "MISSING_METADATA" },
  { value: "INVALID_METADATA", label: "INVALID_METADATA" },
  { value: "CONFLICTING_METADATA", label: "CONFLICTING_METADATA" },
];

interface RequestsViewProps {
  // Query controls & values
  activeTimeRange: TimeRangeKey;
  onSelectTimeRange: (range: TimeRangeKey) => void;
  shortcut: ShortcutFilter;
  onSelectShortcut: (s: ShortcutFilter) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  decisionFilter: DecisionFilter;
  onDecisionChange: (d: DecisionFilter) => void;
  inspectionFilter: InspectionFilter;
  onInspectionChange: (i: InspectionFilter) => void;
  sortField: SortField;
  onSortFieldChange: (field: SortField) => void;
  sortOrder: SortOrder;
  onSortOrderChange: (order: SortOrder) => void;
  page: number;
  onPageChange: (p: number) => void;
  pageSize: number;
  onPageSizeChange: (ps: number) => void;

  // Server-provided table data & counts
  items: RequestRecord[];
  tableTotal: number | null;
  totalPages: number | null;
  sidebarCounts: SidebarCounts | null;

  // Selection & detail drawer
  selectedRequestId: string | null;
  onSelectRequestId: (id: string | null) => void;
  detailRequest: RequestRecord | null;
  detailLoading: boolean;
  detailError: Error | null;
  onDetailRetry?: () => void;

  // Lifecycle & honest status
  isLoading: boolean;
  isRefreshing: boolean;
  isStale: boolean;
  hasNewData: boolean;
  serverTime: string | null;
  error: Error | null;
  onRefresh: () => Promise<void | boolean>;
  onApplyPendingData: () => void;
  isSyntheticMode?: boolean;
}

export const RequestsView: React.FC<RequestsViewProps> = ({
  activeTimeRange,
  onSelectTimeRange,
  shortcut,
  onSelectShortcut,
  searchQuery,
  onSearchChange,
  decisionFilter,
  onDecisionChange,
  inspectionFilter,
  onInspectionChange,
  sortField,
  onSortFieldChange,
  sortOrder,
  onSortOrderChange,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  items,
  tableTotal,
  totalPages,
  sidebarCounts,
  selectedRequestId,
  onSelectRequestId,
  detailRequest,
  detailLoading,
  detailError,
  onDetailRetry,
  isLoading,
  isRefreshing,
  isStale,
  hasNewData,
  serverTime,
  error,
  onRefresh,
  onApplyPendingData,
  isSyntheticMode = false,
}) => {
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  const [isInspectionOpen, setIsInspectionOpen] = useState(false);
  const [isTimeRangeOpen, setIsTimeRangeOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  // Track initiating row ref for focus restoration on drawer close
  const [lastFocusedRowId, setLastFocusedRowId] = useState<string | null>(null);

  // Restore focus to the initiating row after drawer closes
  useEffect(() => {
    if (!selectedRequestId && lastFocusedRowId) {
      const rowEl = document.getElementById(`row-${lastFocusedRowId}`);
      rowEl?.focus();
    }
  }, [selectedRequestId, lastFocusedRowId]);

  const handleOpenRequest = useCallback(
    (req: RequestRecord) => {
      setLastFocusedRowId(req.id);
      onSelectRequestId(req.id);
    },
    [onSelectRequestId]
  );

  const handleCloseRequest = useCallback(() => {
    onSelectRequestId(null);
  }, [onSelectRequestId]);

  const handleResetFilters = () => {
    onSelectShortcut("all");
    onSearchChange("");
    onDecisionChange("ALL");
    onInspectionChange("ALL");
  };

  const hasActiveFilters =
    shortcut !== "all" ||
    searchQuery.trim() !== "" ||
    decisionFilter !== "ALL" ||
    inspectionFilter !== "ALL";

  const activeTimeLabel =
    TIME_RANGE_OPTIONS.find((o) => o.key === activeTimeRange)?.label ?? "All records";

  const isDataAvailable = tableTotal !== null && totalPages !== null;
  const startIndex = isDataAvailable && tableTotal > 0 ? (page - 1) * pageSize + 1 : 0;
  const endIndex = isDataAvailable && tableTotal > 0 ? Math.min(page * pageSize, tableTotal) : 0;

  return (
    <div className="flex flex-grow h-[calc(100vh-48px)] min-w-0 overflow-hidden">
      {/* 240px Contextual Sidebar */}
      <aside
        aria-label="Request shortcuts"
        className="w-60 border-r border-border bg-panel flex flex-col shrink-0 select-none hidden md:flex"
      >
        <div className="h-12 border-b border-border px-4 flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">Requests</span>
          <span className="text-[11px] font-mono text-foreground-dim">
            {sidebarCounts !== null ? `${sidebarCounts.all} records` : "—"}
          </span>
        </div>

        <div className="p-2 space-y-1 text-sm flex-grow">
          <div className="px-2 py-1.5 text-xs font-medium uppercase tracking-wider text-foreground-dim">
            Views
          </div>

          <button
            type="button"
            onClick={() => onSelectShortcut("all")}
            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-left transition-colors ${
              shortcut === "all"
                ? "bg-panel-hover text-foreground font-medium"
                : "text-foreground-muted hover:text-foreground hover:bg-panel-hover"
            }`}
          >
            <div className="flex items-center gap-2.5">
              <ShieldCheck className="w-4 h-4 text-foreground-muted" />
              <span>All requests</span>
            </div>
            <span className="text-xs font-mono text-foreground-dim">
              {sidebarCounts !== null ? sidebarCounts.all : "—"}
            </span>
          </button>

          <button
            type="button"
            onClick={() => onSelectShortcut("blocked")}
            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-left transition-colors ${
              shortcut === "blocked"
                ? "bg-panel-hover text-foreground font-medium"
                : "text-foreground-muted hover:text-foreground hover:bg-panel-hover"
            }`}
          >
            <div className="flex items-center gap-2.5">
              <ShieldX className="w-4 h-4 text-verdict-block" />
              <span>Blocked traffic</span>
            </div>
            <span className="text-xs font-mono text-foreground-dim">
              {sidebarCounts !== null ? sidebarCounts.blocked : "—"}
            </span>
          </button>

          <button
            type="button"
            onClick={() => onSelectShortcut("flagged")}
            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-left transition-colors ${
              shortcut === "flagged"
                ? "bg-panel-hover text-foreground font-medium"
                : "text-foreground-muted hover:text-foreground hover:bg-panel-hover"
            }`}
          >
            <div className="flex items-center gap-2.5">
              <ShieldAlert className="w-4 h-4 text-verdict-flag" />
              <span>Flagged requests</span>
            </div>
            <span className="text-xs font-mono text-foreground-dim">
              {sidebarCounts !== null ? sidebarCounts.flagged : "—"}
            </span>
          </button>

          <button
            type="button"
            onClick={() => onSelectShortcut("failures")}
            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-left transition-colors ${
              shortcut === "failures"
                ? "bg-panel-hover text-foreground font-medium"
                : "text-foreground-muted hover:text-foreground hover:bg-panel-hover"
            }`}
          >
            <div className="flex items-center gap-2.5">
              <AlertTriangle className="w-4 h-4 text-foreground-muted" />
              <span>Inspection failures</span>
            </div>
            <span className="text-xs font-mono text-foreground-dim">
              {sidebarCounts !== null ? sidebarCounts.failures : "—"}
            </span>
          </button>
        </div>
      </aside>

      {/* Main Table Workspace Canvas */}
      <div className="flex flex-col flex-grow min-w-0 bg-canvas overflow-hidden">
        {/* Top Tab Bar */}
        <div className="h-10 border-b border-border bg-panel flex items-center px-4 gap-2 shrink-0 select-none">
          <div className="h-full border-b-2 border-brand px-3 flex items-center gap-2 text-xs font-medium text-foreground bg-canvas/40">
            <span className="font-mono">http_request_logs</span>
            <span className="text-[10px] font-mono px-1 rounded bg-panel-subtle text-foreground-dim">
              {tableTotal !== null ? tableTotal : "—"}
            </span>
          </div>
        </div>

        {/* Stale Cache Notice Banner */}
        {isStale && (
          <div
            data-testid="stale-notice"
            className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/30 flex items-center justify-between text-xs text-amber-200 shrink-0"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span>
                Telemetry data may be stale
                {serverTime ? ` (cached from ${formatDateTimeIST(serverTime)})` : ""}.
                A background refresh failed.
              </span>
            </div>
            <button
              type="button"
              onClick={() => onRefresh()}
              className="px-2.5 py-0.5 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 font-medium transition-colors flex items-center gap-1.5"
            >
              <RotateCw className="w-3 h-3" />
              Retry
            </button>
          </div>
        )}

        {/* Updated Telemetry Available Banner (Later-Page Stability) */}
        {hasNewData && (
          <div
            data-testid="pending-data-banner"
            className="px-4 py-2 bg-brand/10 border-b border-brand/30 flex items-center justify-between text-xs text-foreground shrink-0"
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
              <span>Updated telemetry available while viewing page {page}.</span>
            </div>
            <button
              type="button"
              onClick={onApplyPendingData}
              className="px-2.5 py-0.5 rounded bg-brand text-white font-medium hover:bg-brand-hover transition-colors"
            >
              Update table
            </button>
          </div>
        )}

        {/* Compact Interactive Toolbar */}
        <div className="min-h-11 border-b border-border px-4 py-1.5 flex items-center justify-between gap-2 shrink-0 text-sm select-none flex-wrap">
          {/* Search */}
          <div className="flex items-center gap-2 flex-grow max-w-sm min-w-0">
            <div className="relative w-full">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Filter by path, IP, or ID..."
                className="w-full bg-panel border border-border rounded-md pl-8 pr-7 py-1 text-xs text-foreground placeholder-foreground-dim focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => onSearchChange("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground-dim hover:text-foreground"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {/* Time Range Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsTimeRangeOpen(!isTimeRangeOpen)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-panel border border-border text-xs text-foreground hover:bg-panel-hover transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
              >
                <span>{activeTimeLabel}</span>
                <ChevronDown className="w-3 h-3 text-foreground-muted" />
              </button>

              {isTimeRangeOpen && (
                <>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setIsTimeRangeOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute right-0 mt-1 w-40 rounded-md bg-panel border border-border shadow-xl z-30 py-1 text-xs">
                    {TIME_RANGE_OPTIONS.map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => {
                          onSelectTimeRange(opt.key);
                          setIsTimeRangeOpen(false);
                        }}
                        className={`w-full px-3 py-1.5 text-left flex items-center justify-between transition-colors ${
                          activeTimeRange === opt.key
                            ? "bg-panel-hover text-foreground font-medium"
                            : "text-foreground-muted hover:text-foreground hover:bg-panel-hover/60"
                        }`}
                      >
                        <span>{opt.label}</span>
                        {activeTimeRange === opt.key && (
                          <Check className="w-3 h-3 text-brand" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsSortOpen(!isSortOpen)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-panel border border-border text-xs text-foreground hover:bg-panel-hover transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
              >
                <SlidersHorizontal className="w-3 h-3 text-foreground-muted" />
                <span>Sort</span>
              </button>

              {isSortOpen && (
                <>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setIsSortOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute right-0 mt-1 w-48 rounded-md bg-panel border border-border shadow-xl z-30 py-1 text-xs">
                    {(
                      [
                        ["timestamp", "Timestamp"],
                        ["raw_path", "Path"],
                        ["policy_decision", "Decision"],
                        ["latency_ms", "Total time"],
                        ["blocking_score", "Blocking score"],
                      ] as const
                    ).map(([field, label]) => (
                      <button
                        key={field}
                        type="button"
                        onClick={() => {
                          if (sortField === field) {
                            onSortOrderChange(sortOrder === "asc" ? "desc" : "asc");
                          } else {
                            onSortFieldChange(field);
                            onSortOrderChange("desc");
                          }
                          setIsSortOpen(false);
                        }}
                        className={`w-full px-3 py-1.5 text-left flex items-center justify-between ${
                          sortField === field
                            ? "bg-panel-hover text-foreground font-medium"
                            : "text-foreground-muted hover:text-foreground hover:bg-panel-hover/60"
                        }`}
                      >
                        <span>{label}</span>
                        {sortField === field && (
                          <span className="text-[10px] font-mono text-brand uppercase">
                            {sortOrder}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Decision Filter Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDecisionOpen(!isDecisionOpen)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none ${
                  decisionFilter !== "ALL"
                    ? "bg-brand-subtle border-brand-border text-brand"
                    : "bg-panel border-border text-foreground hover:bg-panel-hover"
                }`}
              >
                <span>
                  {decisionFilter === "ALL"
                    ? "Decision"
                    : decisionFilter === "NOT_EVALUATED"
                    ? "Not evaluated"
                    : decisionFilter}
                </span>
                <ChevronDown className="w-3 h-3 text-foreground-muted" />
              </button>

              {isDecisionOpen && (
                <>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setIsDecisionOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute right-0 mt-1 w-44 rounded-md bg-panel border border-border shadow-xl z-30 py-1 text-xs">
                    {(
                      [
                        ["ALL", "All decisions"],
                        ["ALLOW", "ALLOW"],
                        ["FLAG", "FLAG"],
                        ["BLOCK", "BLOCK"],
                        ["NOT_EVALUATED", "Not evaluated"],
                      ] as const
                    ).map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => {
                          onDecisionChange(val);
                          setIsDecisionOpen(false);
                        }}
                        className={`w-full px-3 py-1.5 text-left flex items-center justify-between transition-colors ${
                          decisionFilter === val
                            ? "bg-panel-hover text-foreground font-medium"
                            : "text-foreground-muted hover:text-foreground hover:bg-panel-hover/60"
                        }`}
                      >
                        <span>{label}</span>
                        {decisionFilter === val && (
                          <Check className="w-3 h-3 text-brand" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Inspection Status Filter Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsInspectionOpen(!isInspectionOpen)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none ${
                  inspectionFilter !== "ALL"
                    ? "bg-brand-subtle border-brand-border text-brand"
                    : "bg-panel border-border text-foreground hover:bg-panel-hover"
                }`}
              >
                <span>
                  {inspectionFilter === "ALL" ? "Inspection" : inspectionFilter}
                </span>
                <ChevronDown className="w-3 h-3 text-foreground-muted" />
              </button>

              {isInspectionOpen && (
                <>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setIsInspectionOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute right-0 mt-1 w-52 rounded-md bg-panel border border-border shadow-xl z-30 py-1 text-xs max-h-72 overflow-y-auto">
                    {INSPECTION_FILTER_OPTIONS.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          onInspectionChange(value);
                          setIsInspectionOpen(false);
                        }}
                        className={`w-full px-3 py-1.5 text-left flex items-center justify-between transition-colors ${
                          inspectionFilter === value
                            ? "bg-panel-hover text-foreground font-medium"
                            : "text-foreground-muted hover:text-foreground hover:bg-panel-hover/60"
                        }`}
                      >
                        <span
                          className={
                            value !== "ALL" && value !== "SUCCESS" && value !== "NOT_RUN"
                              ? "text-verdict-flag"
                              : ""
                          }
                        >
                          {label}
                        </span>
                        {inspectionFilter === value && (
                          <Check className="w-3 h-3 text-brand shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Refresh Action */}
            <button
              type="button"
              onClick={() => onRefresh()}
              aria-label="Refresh table"
              className={`p-1.5 rounded-md bg-panel border border-border text-foreground-muted hover:text-foreground hover:bg-panel-hover transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none ${
                isRefreshing ? "opacity-50 pointer-events-none" : ""
              }`}
            >
              <RotateCw className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin text-brand" : ""}`} />
            </button>
          </div>
        </div>

        {/* Data Table Container */}
        <div className="flex-grow overflow-auto">
          {error && items.length === 0 ? (
            /* Error State: changed-query failure or initial outage */
            <div className="h-64 flex flex-col items-center justify-center text-center p-6 space-y-3">
              <div className="w-9 h-9 rounded-full bg-panel-subtle flex items-center justify-center text-verdict-block">
                <ShieldAlert className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Unable to load requests</p>
                <p className="text-xs text-foreground-muted mt-0.5 max-w-sm">
                  {error.message || "An error occurred while fetching requests from the management API."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onRefresh()}
                className="px-3 py-1.5 rounded-md bg-panel border border-border text-xs font-medium text-foreground hover:bg-panel-hover transition-colors inline-flex items-center gap-1.5"
              >
                <RotateCw className="w-3 h-3" />
                Retry
              </button>
            </div>
          ) : !isLoading && items.length === 0 ? (
            /* Empty State */
            <div className="h-64 flex flex-col items-center justify-center text-center p-6 space-y-3">
              <div className="w-9 h-9 rounded-full bg-panel-subtle flex items-center justify-center text-foreground-dim">
                <Search className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">No requests match your filter</p>
                <p className="text-xs text-foreground-muted mt-0.5">
                  Try adjusting the search query, decision filter, inspection filter, or sidebar view.
                </p>
              </div>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="px-3 py-1.5 rounded-md bg-panel border border-border text-xs font-medium text-foreground hover:bg-panel-hover transition-colors"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <table className="w-full text-left border-collapse min-w-[800px]" aria-busy={isLoading && items.length === 0}>
              {isLoading && items.length === 0 && (
                <caption className="sr-only">Loading request records</caption>
              )}
              <thead>
                <tr className="border-b border-border bg-panel-subtle text-xs font-medium text-foreground-muted uppercase tracking-wider select-none">
                  <th className="w-10 px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      disabled
                      aria-label="Select all rows"
                      className="rounded border-border bg-canvas accent-brand cursor-default"
                    />
                  </th>
                  <th
                    onClick={() => {
                      if (sortField === "timestamp") {
                        onSortOrderChange(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                        onSortFieldChange("timestamp");
                        onSortOrderChange("desc");
                      }
                    }}
                    className="px-3 py-2 cursor-pointer hover:text-foreground transition-colors"
                  >
                    <div className="flex items-center gap-1">
                      <span>Timestamp (IST)</span>
                      {sortField === "timestamp" && (
                        <ArrowUpDown className="w-3 h-3 text-brand" />
                      )}
                    </div>
                  </th>
                  <th className="px-3 py-2">Method</th>
                  <th
                    onClick={() => {
                      if (sortField === "raw_path") {
                        onSortOrderChange(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                        onSortFieldChange("raw_path");
                        onSortOrderChange("asc");
                      }
                    }}
                    className="px-3 py-2 cursor-pointer hover:text-foreground transition-colors"
                  >
                    <div className="flex items-center gap-1">
                      <span>Path</span>
                      {sortField === "raw_path" && (
                        <ArrowUpDown className="w-3 h-3 text-brand" />
                      )}
                    </div>
                  </th>
                  <th className="px-3 py-2">Decision</th>
                  <th className="px-3 py-2">Inspection</th>
                  <th className="px-3 py-2">Response</th>
                  <th
                    onClick={() => {
                      if (sortField === "latency_ms") {
                        onSortOrderChange(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                        onSortFieldChange("latency_ms");
                        onSortOrderChange("desc");
                      }
                    }}
                    className="px-3 py-2 text-right cursor-pointer hover:text-foreground transition-colors"
                  >
                    <div className="flex items-center justify-end gap-1">
                      <span>Total time</span>
                      {sortField === "latency_ms" && (
                        <ArrowUpDown className="w-3 h-3 text-brand" />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                {isLoading && items.length === 0 ? (
                  Array.from({ length: 8 }, (_, i) => (
                    <tr key={`skeleton-row-${i}`} className="border-b border-border/40">
                      <td className="px-3 py-3 text-center">
                        <Skeleton className="w-3.5 h-3.5 mx-auto" />
                      </td>
                      <td className="px-3 py-3">
                        <Skeleton className="h-3.5 w-36" />
                      </td>
                      <td className="px-3 py-3">
                        <Skeleton className="h-5 w-12 rounded" />
                      </td>
                      <td className="px-3 py-3">
                        <Skeleton className="h-3.5 w-48" />
                      </td>
                      <td className="px-3 py-3">
                        <Skeleton className="h-5 w-16 rounded" />
                      </td>
                      <td className="px-3 py-3">
                        <Skeleton className="h-3.5 w-20" />
                      </td>
                      <td className="px-3 py-3">
                        <Skeleton className="h-3.5 w-8" />
                      </td>
                      <td className="px-3 py-3 text-right">
                        <Skeleton className="h-3.5 w-12 ml-auto" />
                      </td>
                    </tr>
                  ))
                ) : (
                  items.map((req) => (
                  <tr
                    key={req.id}
                    id={`row-${req.id}`}
                    onClick={() => handleOpenRequest(req)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOpenRequest(req);
                      }
                    }}
                    className="hover:bg-panel-hover/80 cursor-pointer transition-colors focus-visible:bg-panel-active focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand focus-visible:ring-inset"
                  >
                    <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        disabled
                        aria-label={`Select row ${req.id}`}
                        className="rounded border-border bg-canvas accent-brand cursor-default"
                      />
                    </td>
                    <td className="px-3 py-2.5 text-foreground-muted text-xs font-mono whitespace-nowrap">
                      {formatDateTimeIST(req.timestamp)}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-panel-subtle border border-border text-foreground font-medium">
                        {req.method}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-foreground font-medium max-w-[260px] truncate">
                      {req.raw_path}
                      {req.query_string ? (
                        <span className="text-foreground-dim">?{req.query_string}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <span
                        className={`text-xs px-2 py-0.5 rounded font-medium ${
                          req.policy_decision === "ALLOW"
                            ? "bg-verdict-allow-bg text-verdict-allow border border-verdict-allow/30"
                            : req.policy_decision === "BLOCK"
                            ? "bg-verdict-block-bg text-verdict-block border border-verdict-block/30"
                            : req.policy_decision === "FLAG"
                            ? "bg-verdict-flag-bg text-verdict-flag border border-verdict-flag/30"
                            : "bg-verdict-neutral-bg text-verdict-neutral border border-border"
                        }`}
                      >
                        {req.policy_decision === null ? "Not evaluated" : req.policy_decision}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-foreground-muted whitespace-nowrap">
                      <span
                        className={
                          INSPECTION_FAILURE_STATUSES.has(req.inspection_status)
                            ? "text-verdict-flag font-medium"
                            : ""
                        }
                      >
                        {req.inspection_status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-foreground whitespace-nowrap">
                      {req.response_status}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-xs text-foreground-muted tabular-nums whitespace-nowrap">
                      {req.latency_ms} ms
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer / Pagination Bar */}
        <div className="h-10 border-t border-border px-4 bg-panel flex items-center justify-between text-xs text-foreground-muted shrink-0 select-none">
          <div className="flex items-center gap-3">
            {!isDataAvailable ? (
              <span className="text-foreground-dim">{error ? "Unavailable" : "—"}</span>
            ) : (
              <>
                <span>
                  Page {page} of {Math.max(1, totalPages)}
                </span>
                <span>•</span>
                <span>
                  Showing {startIndex}–{endIndex} of {tableTotal} records
                </span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              disabled={!isDataAvailable || isLoading}
              aria-label="Page size"
              className="bg-canvas border border-border text-foreground rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-brand disabled:opacity-40 disabled:pointer-events-none"
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>

            <button
              type="button"
              disabled={!isDataAvailable || isLoading || page <= 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
              className="p-1 rounded border border-border bg-panel text-foreground-muted hover:text-foreground disabled:opacity-40 disabled:pointer-events-none transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              disabled={!isDataAvailable || isLoading || page >= (totalPages ?? 1)}
              onClick={() => onPageChange(Math.min(totalPages ?? 1, page + 1))}
              className="p-1 rounded border border-border bg-panel text-foreground-muted hover:text-foreground disabled:opacity-40 disabled:pointer-events-none transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Selected Request Detail Panel */}
      <RequestDetailPanel
        isOpen={selectedRequestId !== null}
        requestId={selectedRequestId}
        request={detailRequest}
        isLoading={detailLoading}
        error={detailError}
        onRetry={onDetailRetry}
        onClose={handleCloseRequest}
        isSyntheticMode={isSyntheticMode}
      />
    </div>
  );
};
